import 'react/jsx-runtime'

import { ThemeProvider } from "@/components/providers/theme-provider"
import * as Sentry from '@sentry/react'
import { PropsWithChildren, StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter as Router, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType, useRoutes } from 'react-router-dom'

import routes from '~react-pages'

import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import press from "virtual:press"
import { AppSettings } from './AppSettings'
import MuiXLicense from './components/MuiXLicense'
import AuthenticatedUserProvider from './components/providers/AuthenticatedUserProvider'
import ChosenThemeProvider from './components/providers/styles/ChosenThemeProvider'
import DefaultAfterThemeCreated from './components/styles/DefaultAfterThemeCreated'
import DefaultBeforeThemeCreated from './components/styles/DefaultBeforeThemeCreated'
import { PressContext } from './contexts'
import ServiceStackClientProvider from './components/providers/api/ServiceStackClientProvider'
import { createSentryAnonymousUser } from './errors'

const sentryIntegrations = AppSettings.sentry.dsn
    ? [
        Sentry.reactRouterBrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            networkDetailAllowUrls: [window.location.origin],
            networkCaptureBodies: true,
        }),
    ]
    : [];

if (AppSettings.sentry.dsn && AppSettings.sentry.enableReportABug) {
    sentryIntegrations.push(Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: 'system',
        showBranding: false,
        showName: false,
        showEmail: false,
        tags: {
            Application: AppSettings.sentry.application,
            Customer: AppSettings.sentry.customer,
        },
        useSentryUser: {
            name: 'fullName',
            email: 'email',
        },
    }));
}

if (AppSettings.sentry.dsn) {
    Sentry.init({
        dsn: AppSettings.sentry.dsn,
        sendDefaultPii: AppSettings.sentry.sendDefaultPii,
        enableMetrics: AppSettings.sentry.enableMetrics,
        attachStacktrace: AppSettings.sentry.attachStacktrace,
        debug: AppSettings.sentry.debug,
        enableLogs: AppSettings.sentry.enableLogs,
        integrations: sentryIntegrations,
        environment: AppSettings.sentry.environment,
        release: AppSettings.sentry.release,
        tracesSampleRate: AppSettings.sentry.tracesSampleRate,
        tracePropagationTargets: ['localhost', /^\//, window.location.origin],
        replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate,
        replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,
    });

    Sentry.setTags({
        Application: AppSettings.sentry.application,
        Customer: AppSettings.sentry.customer,
    });
    Sentry.setUser(createSentryAnonymousUser());
}

const useRoutesWithSentry = AppSettings.sentry.dsn ? Sentry.wrapUseRoutes(useRoutes) : useRoutes;

const errorFallback = (
    <div role="alert">
        An error has occurred. Please refresh the page and try again.
    </div>
);
import { SnackbarProvider } from 'notistack'

const ApplicationErrorBoundary = ({ children }: PropsWithChildren) => {
    if (!AppSettings.sentry.dsn) {
        return <>{children}</>;
    }

    return (
        <Sentry.ErrorBoundary fallback={errorFallback}>
            {children}
        </Sentry.ErrorBoundary>
    );
};

const theme = createTheme({
    palette: {
        secondary: { main: '#EC008C' },
        primary: { main: '#56287B', dark: '#262626' },

        background: {
            default: '#eeeeee',
            paper: '#ffffff'

        },
    },
});



function App() {
    return (
    <>
        <MuiXLicense />
        <MuiThemeProvider theme={theme}>
        <ServiceStackClientProvider baseUrl={AppSettings.client?.baseUrl} basePath={AppSettings.client?.basePath}>

            <PressContext.Provider value={press}>
                <AuthenticatedUserProvider>
                    <ChosenThemeProvider>
                        <ChosenThemeProvider override={AppSettings.overrideTheme}>
                            <ThemeProvider
                                beforeThemeCreated={DefaultBeforeThemeCreated}
                                afterThemeCreated={DefaultAfterThemeCreated}
                            >
                                <PressContext.Provider value={press}>
                                {useRoutesWithSentry(routes)}
                            </PressContext.Provider>
                            </ThemeProvider>
                        </ChosenThemeProvider>
                    </ChosenThemeProvider>
                </AuthenticatedUserProvider>
            </PressContext.Provider>
        </ServiceStackClientProvider>
        </MuiThemeProvider>
    </>
    
    )
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

const app = AppSettings.sentry.dsn
    ? createRoot(document.getElementById('root')!, {
        onRecoverableError: Sentry.reactErrorHandler(),
    })
    : createRoot(document.getElementById('root')!);

app.render(
    <StrictMode>
        <Router>
            <ScrollToTop />
            <ApplicationErrorBoundary>
                <SnackbarProvider>
                    <App />
                </SnackbarProvider>
            </ApplicationErrorBoundary>
        </Router>
    </StrictMode>,
)
